import type { ApprovedFormulaDefinition, ApprovedFormulaResult } from './approved-formulas';
import {
  financeMarginTargetLevel,
  type FinanceMarginTargetType,
} from './finance-ebitda-target-resolve-formula';

export const FINANCE_INCOME_MARGIN_MEASURE = 'operating_income_revenue_margin_pct';
export const FINANCE_INCOME_MARGIN_FORMULA_ID = 'finance_income_margin_target_v1';
export const FINANCE_INCOME_MARGIN_FORMULA_VERSION = '1.0.0';

export type FinanceIncomeMarginTargetInput = Readonly<{
  metricRef: typeof FINANCE_INCOME_MARGIN_MEASURE;
  targetType: FinanceMarginTargetType;
  targetValue: number;
  operatingIncomeUsd: number;
  revenueUsd: number;
}>;
export type FinanceIncomeMarginTargetOutput = Readonly<{
  baselineMarginPct: number;
  targetMarginPct: number;
  targetIncomeUsd: number;
  gapUsd: number;
  assumption: 'unchanged_revenue';
}>;

export function executeFinanceIncomeMarginTarget(
  input: FinanceIncomeMarginTargetInput
): ApprovedFormulaResult<FinanceIncomeMarginTargetOutput> {
  if (
    input?.metricRef !== FINANCE_INCOME_MARGIN_MEASURE ||
    ![input?.targetValue, input?.operatingIncomeUsd, input?.revenueUsd].every(Number.isFinite) ||
    input.revenueUsd <= 0
  )
    return invalidInput();
  const baselineMarginPct = (input.operatingIncomeUsd / input.revenueUsd) * 100;
  const targetMarginPct = financeMarginTargetLevel(
    input.targetType,
    baselineMarginPct,
    input.targetValue
  );
  if (targetMarginPct === undefined || !Number.isFinite(targetMarginPct)) return invalidInput();
  const targetIncomeUsd = Math.round(input.revenueUsd * targetMarginPct) / 100;
  const gapUsd = Math.round((targetIncomeUsd - input.operatingIncomeUsd) * 100) / 100;
  if (![targetIncomeUsd, gapUsd].every(Number.isFinite)) return invalidInput();
  return {
    status: 'ok',
    output: {
      baselineMarginPct: Math.round(baselineMarginPct * 10_000) / 10_000,
      targetMarginPct: Math.round(targetMarginPct * 10_000) / 10_000,
      targetIncomeUsd,
      gapUsd,
      assumption: 'unchanged_revenue',
    },
  };
}

function invalidInput(): ApprovedFormulaResult<FinanceIncomeMarginTargetOutput> {
  return {
    status: 'blocked',
    reasonCode: 'invalid_formula_input',
    message:
      'A supported margin target, finite reported operating income and positive gross revenue are required.',
  };
}

type Registration = Pick<
  ApprovedFormulaDefinition,
  'expectedContextKind' | 'approvedSourceSystemRefs' | 'implementationDigestInputs'
>;
export function createFinanceIncomeMarginTargetDefinition(
  registration: Registration
): ApprovedFormulaDefinition<FinanceIncomeMarginTargetInput, FinanceIncomeMarginTargetOutput> {
  return {
    ...registration,
    formulaId: FINANCE_INCOME_MARGIN_FORMULA_ID,
    version: FINANCE_INCOME_MARGIN_FORMULA_VERSION,
    displayName: 'Reported operating income / gross revenue target',
    description:
      'Resolve a selected margin target against reported income and unchanged gross revenue from one reporting cut.',
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: 'finance_income_margin_target_input_v1',
    outputSchemaVersion: 'finance_income_margin_target_output_v1',
    goldenTraces: (
      [
        ['margin_relative_increase_pct', 10.35, 103_500, 13_500],
        ['margin_point_increase', 24, 240_000, 150_000],
      ] as const
    ).map(([targetType, targetMarginPct, targetIncomeUsd, gapUsd]) => ({
      traceId: `income-margin:${targetType}`,
      input: {
        metricRef: FINANCE_INCOME_MARGIN_MEASURE,
        targetType,
        targetValue: 15,
        operatingIncomeUsd: 90_000,
        revenueUsd: 1_000_000,
      },
      expected: {
        status: 'ok' as const,
        output: {
          baselineMarginPct: 9,
          targetMarginPct,
          targetIncomeUsd,
          gapUsd,
          assumption: 'unchanged_revenue' as const,
        },
      },
    })),
    execute: executeFinanceIncomeMarginTarget,
  };
}
